import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Plus, Trash2, Save, Loader2, Navigation } from 'lucide-react';
import toast from 'react-hot-toast';
import { updateFacility } from '../../../../../../services/staffingService';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function mapsLink(lng, lat) {
  return `https://www.google.com/maps?q=${lat},${lng}`;
}
function streetViewLink(lng, lat) {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

/**
 * PostsTab — manage security checkpoint posts on a map.
 * Right-click the map to add a post with a note.
 * Props: facility, customerCompanyId, onFacilityUpdate(updated)
 */
const PostsTab = ({ facility, customerCompanyId, onFacilityUpdate }) => {
  const mapRef            = useRef(null);
  const mapInstance       = useRef(null);
  const markersRef        = useRef([]);
  const facilityMarkerRef = useRef(null);

  const [posts,   setPosts]   = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [pending, setPending] = useState(null); // { lat, lng } for new post being added
  const [note,    setNote]    = useState('');
  const [dirty,   setDirty]   = useState(false);

  // Initialise posts from facility prop
  useEffect(() => {
    const mapped = (facility.posts || []).map(p => ({
      lng:  p.position?.coordinates?.[0] ?? 0,
      lat:  p.position?.coordinates?.[1] ?? 0,
      note: p.note || '',
    }));
    setPosts(mapped);
    setDirty(false);
  }, [facility]);

  // Initialise Google Map
  useEffect(() => {
    if (!window.google?.maps || !mapRef.current) return;
    const center = posts.length > 0
      ? { lat: posts[0].lat, lng: posts[0].lng }
      : { lat: facility.geoLocation?.lat ?? 42.3601, lng: facility.geoLocation?.lng ?? -71.0589 };

    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 16,
      mapTypeId: 'satellite',
      disableDefaultUI: false,
    });

    // Main facility location marker (pink pin)
    const facilityLat = facility.geoLocation?.lat;
    const facilityLng = facility.geoLocation?.lng;
    if (facilityLat && facilityLng) {
      const svgPin = {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
            <path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 24 16 24S32 26 32 16C32 7.163 24.837 0 16 0z"
              fill="#CC457D" stroke="white" stroke-width="1.5"/>
            <rect x="9" y="9" width="14" height="14" rx="2" fill="white" opacity="0.9"/>
            <path d="M12 19v-5h3v2h2v-2h3v5h-2v-2h-4v2z" fill="#CC457D"/>
          </svg>`)}`,
        scaledSize: new window.google.maps.Size(32, 40),
        anchor: new window.google.maps.Point(16, 40),
      };
      facilityMarkerRef.current = new window.google.maps.Marker({
        position: { lat: facilityLat, lng: facilityLng },
        map: mapInstance.current,
        title: facility.name || 'Facility',
        icon: svgPin,
        zIndex: 1,
      });
    }

    // Right-click to add post
    mapInstance.current.addListener('rightclick', (e) => {
      setPending({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      setNote('');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync markers to posts state
  const syncMarkers = useCallback(() => {
    if (!mapInstance.current) return;
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = posts.map((p, i) => {
      const marker = new window.google.maps.Marker({
        position: { lat: p.lat, lng: p.lng },
        map: mapInstance.current,
        label: { text: String(i + 1), color: 'white', fontWeight: 'bold', fontSize: '12px' },
        title: p.note,
      });
      return marker;
    });
  }, [posts]);

  useEffect(() => { syncMarkers(); }, [syncMarkers]);

  const addPost = () => {
    if (!note.trim()) return toast.error('Add a note for this post');
    const newPost = { lat: pending.lat, lng: pending.lng, note: note.trim() };
    setPosts(p => [...p, newPost]);
    setPending(null);
    setNote('');
    setDirty(true);
  };

  const removePost = (idx) => {
    setPosts(p => p.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = posts.map(p => ({
        position: { lng: p.lng, lat: p.lat },
        note: p.note,
      }));
      const resp = await updateFacility(customerCompanyId, facility._id, { posts: payload });
      onFacilityUpdate(resp.data);
      setDirty(false);
      toast.success('Security posts saved');
    } catch {
      toast.error('Failed to save posts');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4 flex-shrink-0">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Navigation size={14} className="text-[#404880]" /> Security Posts
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">Right-click the map to add a checkpoint post</p>
          <div className="mt-1.5 flex items-center gap-3">
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="inline-block h-3 w-3 rounded-full bg-[#CC457D]" /> Facility location
            </span>
            <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span className="inline-flex h-3 w-3 items-center justify-center rounded-full bg-[#404880] text-[7px] font-bold text-white">1</span> Security post
            </span>
          </div>
        </div>
        {dirty && (
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-[#404880] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save changes
          </button>
        )}
      </div>

      {/* Map */}
      <div className="relative flex-1 min-h-0">
        <div ref={mapRef} className="w-full h-full" style={{ minHeight: 360 }} />

        {/* Right-click popover */}
        {pending && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 w-72 rounded-xl border border-border bg-card shadow-lg p-4">
            <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1">
              <MapPin size={12} className="text-[#CC457D]" /> New Post at {pending.lat.toFixed(5)}, {pending.lng.toFixed(5)}
            </p>
            <input
              autoFocus
              value={note}
              onChange={e => setNote(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addPost()}
              placeholder="Post note (e.g. North entrance)"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#404880] bg-background mb-2"
            />
            <div className="flex gap-2">
              <button onClick={addPost}
                className="flex-1 rounded-lg bg-[#404880] py-1.5 text-xs font-medium text-white hover:opacity-90">
                <Plus size={12} className="inline mr-1" /> Add Post
              </button>
              <button onClick={() => setPending(null)}
                className="flex-1 rounded-lg border border-border py-1.5 text-xs text-muted-foreground hover:bg-muted">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Posts list */}
      <div className="flex-shrink-0 border-t border-border bg-card max-h-48 overflow-y-auto">
        {posts.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">No posts added yet — right-click the map to add one</p>
        ) : (
          <div className="divide-y divide-border">
            {posts.map((p, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#404880] text-[10px] font-bold text-white">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{p.note}</p>
                  <div className="flex gap-3 mt-0.5">
                    <a href={mapsLink(p.lng, p.lat)} target="_blank" rel="noreferrer"
                      className="text-[10px] text-[#404880] hover:underline">Maps</a>
                    <a href={streetViewLink(p.lng, p.lat)} target="_blank" rel="noreferrer"
                      className="text-[10px] text-[#404880] hover:underline">Street View</a>
                  </div>
                </div>
                <button onClick={() => removePost(i)}
                  className="flex-shrink-0 p-1 text-muted-foreground hover:text-red-500 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PostsTab;
